import React, { Component } from "react";
import {
  DateRangePicker,
  Select,
  toFixed,
  format_date,
  GET,
  loading,
  Navbar,
  XLSX,
  saveAs,
} from "../../components/CustomComponent.js";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import GatewayLogo from "../../components/GatewayLogo.js";

// โลโก้ธนาคารจาก global.bank; code ที่ไม่มีรูป (เช่น UNKNOWN) ใช้ bank.png
const BankLogo = ({ code, size = 18 }) => (
  <img
    alt="bank"
    src={require(`../../assets/images/bank/${global.bank?.find((b) => b.value === code)?.icon || "bank.png"}`)}
    width={size}
    height={size}
    style={{ objectFit: "contain", flexShrink: 0 }}
  />
);

// สีประจำฝั่งบนกราฟ: deposit = teal, withdrawal = amber (ชุดเดียวกับ report อื่น)
const SIDE_COLORS = { deposit: "13, 148, 136", withdrawal: "217, 119, 6" };

const GROUP_OPTIONS = [
  { key: "day", label: "Daily", unit: "day" },
  { key: "month", label: "Monthly", unit: "month" },
  { key: "year", label: "Yearly", unit: "year" },
];
const CHART_METRICS = [
  { key: "orders", label: "Transactions" },
  { key: "avg_amount", label: "Avg / Txn" },
  { key: "amount", label: "Amount" },
];
const PERIOD_COLUMNS = [
  "Period",
  "Deposit Txns", "Deposit Amount", "Deposit Avg / Txn",
  "Withdrawal Txns", "Withdrawal Amount", "Withdrawal Avg / Txn",
  "Total Txns", "Total Amount", "Total Avg / Txn",
];
// ตัวกรองแบบเลือกค่า (ไม่รวมวันที่ / สกุลเงิน / การจัดกลุ่ม) ใช้นับจำนวนที่เปิดอยู่และปุ่ม Clear
const PICK_FILTERS = ["filter_company", "filter_gateway", "filter_broker", "filter_bank"];
const EMPTY_PICKS = Object.fromEntries(PICK_FILTERS.map((k) => [k, ""]));

export default class TransactionAnalytics extends Component {
  constructor(props) {
    super(props);
    const now = new Date();
    this.state = {
      filter_start_date: new Date(now.getFullYear(), now.getMonth(), 1),
      filter_end_date: now,
      filter_currency: "THB",
      filter_group: "day",
      filter_company: "",
      filter_gateway: "",
      filter_bank: "",
      filter_broker: "",
      chart_metric: "orders",
      options: { gateways: [], banks: [], brokers: [], companies: [] },
      report: null,
      loaded: false,
    };
    this.request_id = 0;
  }

  async componentDidMount() {
    ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
    loading(false);
    this.GetOptions();
    await this.Get();
  }

  GetOptions = async () => {
    const result = await GET("v1/admin/transaction-analytics/options", null);
    if (result?.status) {
      this.setState({
        options: {
          gateways: (result.data?.gateways || []).map((g) => ({
            ...g,
            front: <span className="me-2"><GatewayLogo name={g.value} size={18} /></span>,
          })),
          banks: (result.data?.banks || []).map((b) => ({
            value: b.value,
            label: b.value,
            front: <span className="me-2"><BankLogo code={b.value} /></span>,
          })),
          brokers: result.data?.brokers || [],
          // 1 ตัวเลือก = 1 บริษัท รวมทุก gateway (gateways / broker_uids ใช้แคบรายการตาม filter อื่น)
          companies: (result.data?.companies || []).map((c) => ({
            ...c,
            gateways: c.gateways || [],
            broker_uids: c.broker_uids || [],
          })),
        },
      });
    }
  };

  Get = async () => {
    const {
      filter_start_date, filter_end_date, filter_currency, filter_group,
      filter_company, filter_gateway, filter_bank, filter_broker,
    } = this.state;
    // เลือกวันเริ่มแล้วแต่ยังไม่เลือกวันสิ้นสุด: รอให้ครบก่อน (ว่างทั้งคู่ = ทั้งหมด)
    if (Boolean(filter_start_date) !== Boolean(filter_end_date)) return;
    const request_id = ++this.request_id;
    loading(true);
    const result = await GET("v1/admin/transaction-analytics", {
      currency: filter_currency,
      group_by: filter_group,
      ...(filter_start_date
        ? {
            start_date: format_date(filter_start_date, "yyyy-mm-dd", "en"),
            end_date: format_date(filter_end_date, "yyyy-mm-dd", "en"),
          }
        : {}),
      ...(filter_company ? { company_id: filter_company } : {}),
      ...(filter_gateway ? { gateway: filter_gateway } : {}),
      ...(filter_bank ? { bank_code: filter_bank } : {}),
      ...(filter_broker ? { broker_id: filter_broker } : {}),
    });
    // ผลของ request เก่าที่มาช้ากว่าตัวล่าสุดไม่เอามาทับ
    if (request_id !== this.request_id) return;
    this.setState({ report: result?.status ? result.data : null, loaded: true });
    loading(false);
  };

  SetFilter = (patch) => this.setState(patch, () => this.Get());

  // company ผูกกับ gateway และ broker: เปลี่ยนสองตัวนั้นแล้ว company ที่เลือกอยู่ไม่เข้าเงื่อนไขก็ล้างทิ้ง
  SetScopedFilter = (patch) => {
    const next = { ...this.state, ...patch };
    const company = this.state.options.companies.find((c) => c.value === next.filter_company);
    const keep =
      !company ||
      ((!next.filter_gateway || company.gateways.includes(next.filter_gateway)) &&
        (!next.filter_broker || company.broker_uids.includes(next.filter_broker)));
    this.SetFilter(keep ? patch : { ...patch, filter_company: "" });
  };

  BuildRows = (list, nameOf) =>
    list.map((r) => [
      nameOf(r),
      r.deposit.orders, r.deposit.amount, r.deposit.avg_amount,
      r.withdrawal.orders, r.withdrawal.amount, r.withdrawal.avg_amount,
      r.total.orders, r.total.amount, r.total.avg_amount,
    ]);

  ExportExcel = () => {
    const { report, filter_currency } = this.state;
    if (!report) return;
    const workbook = XLSX.utils.book_new();
    const periodRows = this.BuildRows(report.series || [], (r) => r.period);
    periodRows.push(this.BuildRows([report.summary], () => "TOTAL")[0]);
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([PERIOD_COLUMNS, ...periodRows]), "Periods");
    [
      ["Companies", report.companies, "Company"],
      ["Gateways", report.gateways, "Gateway"],
      ["Brokers", report.brokers, "Broker"],
      ["Banks", report.banks, "Bank"],
    ].forEach(([sheet, list, head]) => {
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet([
          [head, ...PERIOD_COLUMNS.slice(1), "Share %"],
          ...(list || []).map((r) => [...this.BuildRows([r], (x) => x.name)[0], r.share]),
        ]),
        sheet
      );
    });
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      `transaction-analytics-${report.start_date || "all"}_${report.end_date}-${report.group_by}-${filter_currency}.xlsx`
    );
  };

  render() {
    const {
      filter_start_date, filter_end_date, filter_currency, filter_group,
      filter_company, filter_gateway, filter_bank, filter_broker,
      chart_metric, options, report, loaded,
    } = this.state;

    const cell = (v) => toFixed(v, 2);
    const num = (v) => Number(v) || 0;
    const cnt = (v) => num(v).toLocaleString();
    const rate = (v) => num(v).toLocaleString(undefined, { maximumFractionDigits: 1 });
    const currencySymbol = filter_currency === "THB" ? "฿" : filter_currency === "LAK" ? "₭" : "";

    const summary = report?.summary;
    const series = report?.series || [];
    const group = GROUP_OPTIONS.find((g) => g.key === (report?.group_by || filter_group)) || GROUP_OPTIONS[0];
    const periods = num(report?.periods);
    const allTime = !filter_start_date && !filter_end_date;
    const rangeLabel = allTime
      ? report?.start_date
        ? `All time · since ${format_date(new Date(report.start_date), "dd/mm/yyyy", "en")}`
        : "All time"
      : filter_start_date && filter_end_date
        ? `${format_date(filter_start_date, "dd/mm/yyyy", "en")} – ${format_date(filter_end_date, "dd/mm/yyyy", "en")}`
        : "Select end date";
    const gatewayOptions = options.gateways.filter((g) => !g.currency || g.currency === filter_currency);
    // company ที่เข้ากับ gateway / broker ที่เลือกอยู่ (ไม่เลือก = ทั้งหมด)
    const companyOptions = options.companies.filter(
      (c) =>
        (!filter_gateway || c.gateways.includes(filter_gateway)) &&
        (!filter_broker || c.broker_uids.includes(filter_broker))
    );
    const activePicks = PICK_FILTERS.filter((k) => this.state[k]).length;
    const hasData = num(summary?.total?.orders) > 0;

    const kpis = summary
      ? [
          {
            label: "Deposit Transactions",
            value: cnt(summary.deposit.orders),
            sub: `≈ ${rate(summary.deposit_per_period)} / ${group.unit} · over ${cnt(periods)} ${group.unit}${periods === 1 ? "" : "s"}`,
            accent: "#14b8a6",
            icon: "",
            plain: true,
          },
          {
            label: "Withdrawal Transactions",
            value: cnt(summary.withdrawal.orders),
            sub: `≈ ${rate(summary.withdrawal_per_period)} / ${group.unit} · over ${cnt(periods)} ${group.unit}${periods === 1 ? "" : "s"}`,
            accent: "#f59e0b",
            icon: "",
            plain: true,
          },
          {
            label: "Avg Deposit / Transaction",
            value: cell(summary.deposit.avg_amount),
            sub: `Total ${currencySymbol}${cell(summary.deposit.amount)}`,
            accent: "#6366f1",
            icon: "",
          },
          {
            label: "Avg Withdrawal / Transaction",
            value: cell(summary.withdrawal.avg_amount),
            sub: `Total ${currencySymbol}${cell(summary.withdrawal.amount)}`,
            accent: "#d4af37",
            icon: "",
            highlight: true,
          },
        ]
      : [];

    const metric = CHART_METRICS.find((m) => m.key === chart_metric) || CHART_METRICS[0];
    const fmtMetric = (v) => (metric.key === "orders" ? cnt(v) : cell(v));
    const chartData = {
      labels: series.map((s) => s.period),
      datasets: [
        { id: "deposit", label: "Deposit", side: "deposit" },
        { id: "withdrawal", label: "Withdrawal", side: "withdrawal" },
      ].map((d) => ({
        id: d.id,
        label: d.label,
        data: series.map((s) => num(s[d.side][metric.key])),
        backgroundColor: `rgba(${SIDE_COLORS[d.side]}, 0.9)`,
        hoverBackgroundColor: `rgba(${SIDE_COLORS[d.side]}, 1)`,
        borderRadius: 4,
        maxBarThickness: 28,
      })),
    };
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8 } },
        tooltip: {
          callbacks: { label: (ctx) => `${ctx.dataset.label}: ${fmtMetric(ctx.parsed.y)}` },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 16 } },
        y: {
          beginAtZero: true,
          grid: { color: "rgba(148,163,184,0.15)" },
          ticks: { callback: (v) => Number(v).toLocaleString() },
        },
      },
    };

    const sideCells = (r) => (
      <>
        <td className="text-center ar-group-start">{cnt(r.deposit.orders)}</td>
        <td className="text-end">{cell(r.deposit.amount)}</td>
        <td className="text-end ar-dep-profit">{cell(r.deposit.avg_amount)}</td>
        <td className="text-center ar-group-start">{cnt(r.withdrawal.orders)}</td>
        <td className="text-end">{cell(r.withdrawal.amount)}</td>
        <td className="text-end ar-wd-profit">{cell(r.withdrawal.avg_amount)}</td>
        <td className="text-center ar-group-start">{cnt(r.total.orders)}</td>
        <td className="text-end">{cell(r.total.amount)}</td>
        <td className="text-end ar-sum-cost">{cell(r.total.avg_amount)}</td>
      </>
    );

    // ตัวเลข In / Out ใต้แต่ละแถว breakdown: สีตามฝั่ง, ค่า 0 จางลง
    const sideMeta = (side, label, r) => {
      const orders = num(r[side].orders);
      return (
        <span className={`ta-meta ta-meta-${side === "deposit" ? "dep" : "wd"} ${orders ? "" : "ta-meta-zero"}`}>
          <i className="ta-meta-dot"></i>
          {label} <b>{cnt(orders)}</b>
          <span className="ta-meta-sep">·</span>
          avg <b>{cell(r[side].avg_amount)}</b>
        </span>
      );
    };

    const withData = (list) => list.filter((r) => num(r.total.orders) > 0).length;
    const gatewayList = report?.gateways || [];
    const companyList = report?.companies || [];
    const breakdowns = [
      {
        title: "By Company",
        caption: `${withData(companyList)} of ${companyList.length} with transactions · click a row to filter`,
        list: companyList,
        filter: "filter_company",
        selected: filter_company,
        options: options.companies,
        label: (r) => r.name,
      },
      {
        title: "By Gateway",
        caption: `${withData(gatewayList)} of ${gatewayList.length} with transactions · click a row to filter`,
        list: gatewayList,
        filter: "filter_gateway",
        selected: filter_gateway,
        options: gatewayOptions,
        label: (r) => r.name,
        logo: (r) => <GatewayLogo name={r.key} size={22} />,
        tags: (r) =>
          (r.deposit_active || r.withdrawal_active) && (
            <span className="ta-side-tags">
              {r.deposit_active && <span className="ta-side-tag ta-side-tag-dep" title="Deposit enabled">IN</span>}
              {r.withdrawal_active && <span className="ta-side-tag ta-side-tag-wd" title="Withdrawal enabled">OUT</span>}
            </span>
          ),
      },
      {
        title: "By Broker",
        caption: `Top ${(report?.brokers || []).length} by transactions · click a row to filter`,
        list: report?.brokers || [],
        filter: "filter_broker",
        selected: filter_broker,
        options: options.brokers,
        label: (r) => r.name,
      },
      {
        title: "By Bank",
        caption: `Top ${(report?.banks || []).length} by transactions · click a row to filter`,
        list: report?.banks || [],
        filter: "filter_bank",
        selected: filter_bank,
        options: options.banks,
        label: (r) => r.key,
        logo: (r) => <BankLogo code={r.key} size={22} />,
      },
    ];
    // company / broker เลือกจากการ์ดแล้วต้องเช็ค company ที่เลือกอยู่ด้วย
    const pickFromCard = (b, r) =>
      (b.filter === "filter_gateway" || b.filter === "filter_broker" ? this.SetScopedFilter : this.SetFilter)({
        [b.filter]: b.selected === r.key ? "" : r.key,
      });

    return (
      <div className="w-100 min-vh-100 premium-report">
        <Navbar />
        <div className="body p-3 mt-3">
          {/* ===== Hero ===== */}
          <div className="ar-hero mb-4">
            <div className="ar-hero-head">
              <div>
                <div className="ar-eyebrow mb-1">Transaction Analytics</div>
                <div className="ar-title">Transaction Analytics</div>
                <div className="ar-subtitle">
                  Deposit and withdrawal frequency and average ticket size per {group.unit}
                </div>
              </div>
              <div className="ar-hero-actions">
                <button className="ar-btn-ghost transaction-analytics.read" onClick={this.ExportExcel}>
                  <span className="icon me-2">{""}</span>Export Excel
                </button>
                <div className="text-end ms-2">
                  <div className="ar-year">
                    {summary ? cnt(summary.total.orders) : "—"}
                    <span className="ar-year-small"> txns</span>
                  </div>
                  <div className="ar-year-caption">
                    <span className="ar-caption-chip ms-2">{rangeLabel}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ตัวกรองรวมแถวเดียว: หัว = สกุลเงิน + การจัดกลุ่ม, แถวล่าง = ช่วงวันที่ + ตัวเลือก */}
            <div className="ar-toolbar ar-filter ta-filter mt-4">
              <div className="ar-filter-head ta-filter-head">
                <div className="ta-filter-head-right">
                  <div className="ta-filter-group">
                    <span className="ar-filter-label">Currency</span>
                    <div className="d-flex align-items-center gap-2">
                      {global.currency?.map((item) => (
                        <button
                          key={item.currency_code}
                          className={`ar-pill ${item.currency_code === filter_currency ? "active" : ""}`}
                          onClick={() =>
                            // gateway / company ผูกกับสกุลเงิน เปลี่ยนสกุลแล้วล้างสองตัวนี้
                            this.SetFilter({ filter_currency: item.currency_code, filter_gateway: "", filter_company: "" })
                          }
                        >
                          {item.image}
                          <span className="ms-1">{item.currency_code}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="ta-filter-group">
                    <span className="ar-filter-label">Group by</span>
                    <div className="ar-seg ta-seg-hero">
                      {GROUP_OPTIONS.map((o) => (
                        <button
                          key={o.key}
                          className={`ar-seg-btn ${filter_group === o.key ? "active" : ""}`}
                          onClick={() => this.SetFilter({ filter_group: o.key })}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="ta-filter-group ta-date-field">
                    <span className="ar-filter-label">Date Range</span>
                    <div className="ta-date-row">
                      <DateRangePicker
                        className="form-control"
                        value={{ start_date: filter_start_date, end_date: filter_end_date }}
                        onChange={(e) => {
                          this.setState(
                            { filter_start_date: e.start_date, filter_end_date: e.end_date },
                            () => this.Get()
                          );
                        }}
                      />
                      <button
                        className={`ar-pill ta-pill-text ${allTime ? "active" : ""}`}
                        title="Include every transaction regardless of date"
                        onClick={() => this.SetFilter({ filter_start_date: null, filter_end_date: null })}
                      >
                        All time
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="ar-filter-row">
                <div className="ar-filter-field">
                  <span className="ar-filter-label">Company</span>
                  <Select
                    className="form-control"
                    placeholder="All companies"
                    options={companyOptions}
                    value={filter_company}
                    onChange={(v) => this.SetFilter({ filter_company: v })}
                  />
                </div>
                <div className="ar-filter-field">
                  <span className="ar-filter-label">Gateway</span>
                  <Select
                    className="form-control"
                    placeholder="All gateways"
                    options={gatewayOptions}
                    value={filter_gateway}
                    onChange={(v) => this.SetScopedFilter({ filter_gateway: v })}
                  />
                </div>
                {options.brokers.length > 0 && (
                  <div className="ar-filter-field">
                    <span className="ar-filter-label">Broker</span>
                    <Select
                      className="form-control"
                      placeholder="All brokers"
                      options={options.brokers}
                      value={filter_broker}
                      onChange={(v) => this.SetScopedFilter({ filter_broker: v })}
                    />
                  </div>
                )}
                <div className="ar-filter-field">
                  <span className="ar-filter-label">Bank</span>
                  <Select
                    className="form-control"
                    placeholder="All banks"
                    options={options.banks}
                    value={filter_bank}
                    onChange={(v) => this.SetFilter({ filter_bank: v })}
                  />
                </div>
                {activePicks > 0 && (
                  <div className="ar-toolbar-actions">
                    <button className="ar-btn-ghost" onClick={() => this.SetFilter({ ...EMPTY_PICKS })}>
                      <span className="icon me-2">{""}</span>Clear
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ===== KPI ===== */}
          {kpis.length > 0 && (
            <div className="row g-3 mb-4">
              {kpis.map((k) => (
                <div className="col-12 col-sm-6 col-xl-3" key={k.label}>
                  <div
                    className={`ar-kpi ${k.highlight ? "ar-kpi-highlight" : ""}`}
                    style={{ "--ar-accent": k.accent }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="ar-kpi-label">{k.label}</div>
                      <div className="ar-kpi-icon">
                        <span className="icon">{k.icon}</span>
                      </div>
                    </div>
                    <div className="ar-kpi-value">
                      {!k.plain && <span className="ar-kpi-currency">{currencySymbol}</span>}
                      {k.value}
                    </div>
                    <div className="ar-kpi-sub">{k.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ===== Trend chart ===== */}
          {hasData && (
            <div className="ar-table-card mb-4">
              <div className="ar-table-head flex-wrap gap-2">
                <div>
                  <div className="ar-table-title">{group.label} Trend</div>
                  <div className="ar-table-caption">
                    {metric.label} per {group.unit} · deposit vs withdrawal · {filter_currency}
                    {report.group_by !== report.group_by_requested && (
                      <span className="ar-chip-empty ms-2">
                        range too long for {report.group_by_requested} view · showing by {report.group_by}
                      </span>
                    )}
                  </div>
                </div>
                <div className="ar-seg">
                  {CHART_METRICS.map((o) => (
                    <button
                      key={o.key}
                      className={`ar-seg-btn ${chart_metric === o.key ? "active" : ""}`}
                      onClick={() => this.setState({ chart_metric: o.key })}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="ar-chart">
                <Bar datasetIdKey="id" options={chartOptions} data={chartData} />
              </div>
            </div>
          )}

          {/* ===== Breakdown: company / gateway / bank / broker ===== */}
          {loaded && report && (
            <div className="row g-3 mb-4">
              {breakdowns.map((b) => {
                const max = Math.max(1, ...b.list.map((r) => num(r.total.orders)));
                return (
                  <div className="col-12 col-md-6 col-xxl-3" key={b.title}>
                    <div className="ar-table-card h-100">
                      <div className="ar-table-head">
                        <div>
                          <div className="ar-table-title">{b.title}</div>
                          <div className="ar-table-caption">{b.caption}</div>
                        </div>
                      </div>
                      <div className="ta-breakdown">
                        {b.list.length === 0 && (
                          <div className="ta-breakdown-none">No transactions for {rangeLabel} · {filter_currency}</div>
                        )}
                        {b.list.map((r) => {
                          const canFilter = b.options.some((o) => o.value === r.key);
                          const empty = num(r.total.orders) === 0;
                          return (
                            <div
                              key={r.key || "__none"}
                              className={`ta-breakdown-row ${canFilter ? "ta-clickable" : ""} ${b.selected && b.selected === r.key ? "active" : ""} ${empty ? "ta-breakdown-empty" : ""}`}
                              onClick={() => canFilter && pickFromCard(b, r)}
                            >
                              <div className="d-flex justify-content-between align-items-baseline gap-2">
                                <div className="ta-breakdown-name d-flex align-items-center gap-2 text-truncate" title={r.name}>
                                  {b.logo?.(r)}
                                  <span className="text-truncate">
                                    {b.label(r)}
                                    {b.sub?.(r) && <span className="ta-breakdown-sub"> · {b.sub(r)}</span>}
                                  </span>
                                  {b.tags?.(r)}
                                </div>
                                <div className="ta-breakdown-value">
                                  {cnt(r.total.orders)}
                                  <span className="ta-breakdown-share ms-2">{toFixed(r.share, 1)}%</span>
                                </div>
                              </div>
                              <div className="ta-breakdown-bar">
                                <span
                                  className="ta-bar-dep"
                                  style={{ width: `${(num(r.deposit.orders) / max) * 100}%` }}
                                ></span>
                                <span
                                  className="ta-bar-wd"
                                  style={{ width: `${(num(r.withdrawal.orders) / max) * 100}%` }}
                                ></span>
                              </div>
                              <div className="ta-breakdown-meta">
                                {sideMeta("deposit", "In", r)}
                                {sideMeta("withdrawal", "Out", r)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ===== Period table ===== */}
          <div className="ar-table-card">
            <div className="ar-table-head">
              <div>
                <div className="ar-table-title">{group.label} Breakdown</div>
                <div className="ar-table-caption">
                  {rangeLabel} · {cnt(periods)} {group.unit}{periods === 1 ? "" : "s"} · latest first · successful transactions only
                </div>
              </div>
              <div className="d-none d-md-flex align-items-center gap-3 ar-legend">
                <span><i className="ar-legend-dot ar-legend-dep"></i>Deposit</span>
                <span><i className="ar-legend-dot ar-legend-wd"></i>Withdrawal</span>
                <span><i className="ar-legend-dot ar-legend-sum"></i>Total</span>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover text-nowrap ar-table">
                <thead>
                  <tr>
                    <td rowSpan={2} className="align-middle ar-sticky">Period</td>
                    <td colSpan={3} className="text-center ar-group ar-group-dep ar-group-start">
                      <span className="icon me-2">{""}</span>Deposit
                    </td>
                    <td colSpan={3} className="text-center ar-group ar-group-wd ar-group-start">
                      <span className="icon me-2">{""}</span>Withdrawal
                    </td>
                    <td colSpan={3} className="text-center ar-group ar-group-sum ar-group-start">
                      <span className="icon me-2">{""}</span>Total
                    </td>
                  </tr>
                  <tr>
                    {[0, 1, 2].map((i) => (
                      <React.Fragment key={i}>
                        <td className="text-center ar-group-start">Txns</td>
                        <td className="text-end">Amount</td>
                        <td className="text-end ar-head-profit" title="Amount ÷ transactions">Avg / Txn</td>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {hasData &&
                    [...series].reverse().map((s) => (
                      <tr key={s.period} className={num(s.total.orders) === 0 ? "ar-row-empty" : ""}>
                        <td className="ar-sticky">
                          <span className="ar-broker">{s.period}</span>
                        </td>
                        {sideCells(s)}
                      </tr>
                    ))}
                  {hasData && (
                    <tr className="ar-total">
                      <td className="ar-sticky">
                        <div className="ar-month-badge">
                          <span className="ar-month-badge-total">Σ</span>
                          TOTAL
                        </div>
                      </td>
                      {sideCells(summary)}
                    </tr>
                  )}
                  {loaded && !hasData && (
                    <tr>
                      <td colSpan={10} className="text-center py-5">
                        <div className="ar-empty">
                          <span className="icon d-block mb-2">{""}</span>
                          No transactions for {rangeLabel} · {filter_currency}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
